import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import * as API_END_POINT from '../../utilities/constant';
import { useStore } from '../../context/StoreContext';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import {
    Button,
    CollectionRow,
    CollectionRowSkeleton,
    ProductCard,
    ShareMeta,
    collectionImageOf,
} from '../../shared/components';

/* COLLECTIONS — the partner's curated sets.
   Ported from SP-main's `shop-collections.html` and `SF.collRow`.

   ⚠ This is not a browse page: no rail, no sort, no chips, no facets. Reaching
   for `Browse` here because Shop All and Brands use it would be a worse port.
   What it shares with them is the page frame — `.br-wrap`, `.br-h1`,
   `.br-intro`, `.br-empty` — and nothing else.

   Collections are a primary experience, not a merchandising feature, so each one
   gets a row with real products rather than a name in a list.

   ⛔ No carousel: NN/g's research is consistent that people scroll past them and
   lose track past roughly five items. A carousel is defensible on Home, where
   collections are one section among many; here collections ARE the content.

   ⛔ Do not re-benchmark against big-catalogue retailers. A cover-card lead band
   is correct where a collection is a filter into tens of thousands of items; a
   ShopDot partner runs a modest set of kits, and hiding every product behind a
   click is friction.

   A new partner has none — onboarding creates no collections, so the empty state
   is the day-one state for every new store. */

/* The designer owns "featured", not the collection: `page_data.featuredCollections`
   is the one source and its order is the partner's pick order.

   ⛔ Two shapes reach this and both are live (checked against the published
   payload, 2026-09-21):
       { id, title, subtitle, visible, collectionIds: ["19"] }   ← current
       { id, title, subtitle, collectionId: "17" }               ← older, still stored
   Reading only one silently drops half the partner's picks — the page still
   renders, just in the wrong order. Reconcile at the read, not in the data.

   IDs are strings in this payload and numbers on the collection record, so every
   comparison goes through String(). */
const featuredCollectionIds = (featured) => {
    if (!Array.isArray(featured)) return [];
    const out = [];
    featured.forEach((section) => {
        /* A section hidden in the designer contributes no picks. `visible` is
           absent on the older shape, and absent means visible. */
        if (section?.visible === false) return;
        const ids = Array.isArray(section?.collectionIds)
            ? section.collectionIds
            : (section?.collectionId != null ? [section.collectionId] : []);
        ids.forEach((id) => {
            if (id == null || id === '') return;
            const key = String(id);
            if (!out.includes(key)) out.push(key);
        });
    });
    return out;
};

/* The subtitle the partner wrote in the designer, by collection id. */
const featuredSubtitles = (featured) => {
    const map = {};
    if (!Array.isArray(featured)) return map;
    featured.forEach((section) => {
        const subtitle = (section?.subtitle || '').trim();
        if (!subtitle) return;
        const ids = Array.isArray(section?.collectionIds)
            ? section.collectionIds
            : (section?.collectionId != null ? [section.collectionId] : []);
        ids.forEach((id) => {
            if (id != null && id !== '' && !map[String(id)]) map[String(id)] = subtitle;
        });
    });
    return map;
};

const Collections = () => {
    const { pageDetails, storeDetails } = useStore();
    const navigate = useNavigate();

    const [collections, setCollections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchCollections = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(API_END_POINT.COLLECTION_LIST, {
                /* One page: no pager on this surface — a partner runs a modest
                   set of kits, and the reference renders all of them. */
                paging: { limit: 100, offset: 0 },
                sort: [],
            });
            setCollections(response?.data?.data?.collections || []);
        } catch (error) {
            console.error('Error fetching collections:', error);
            setCollections([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchCollections(); }, [fetchCollections]);

    const featured = pageDetails?.page_data?.featuredCollections;

    const ordered = useMemo(() => {
        /* ⛔ A collection with nothing behind it is dropped, not drawn as a
           heading over an empty grid, which reads as a broken page. The drop
           tests the products we can SHOW, not `product_count`, which is the
           collection's own and can outlive what is visible. */
        const live = (collections || []).filter((c) => (
            String(c?.status || '').toUpperCase() === 'PUBLISHED'
            && (c?.products || []).length > 0
        ));

        const picks = featuredCollectionIds(featured);
        const byId = {};
        live.forEach((c) => { byId[String(c.id)] = c; });

        /* Picks that are no longer published are simply not in `byId`, which is
           why this reads the picks through the live set. */
        const feats = picks.map((id) => byId[id]).filter(Boolean);
        const seen = {};
        feats.forEach((c) => { seen[String(c.id)] = 1; });

        /* Featured picks lead, the rest follow in their own order, nothing
           twice — so featuring still buys the partner something. Deliberately no
           separate band and no "More collections" heading: one rhythm down the
           page is what makes ten of them scannable. */
        return feats.concat(live.filter((c) => !seen[String(c.id)]));
    }, [collections, featured]);

    const subtitles = useMemo(() => featuredSubtitles(featured), [featured]);

    /* The partner's own words win over the stored description: `description` is
       seeded with the portal's placeholder copy and most stores never edit it,
       so printing it would put instructions to the retailer on the customer's
       page. The designer's subtitle is what the partner wrote for this
       surface. */
    const describe = (c) => subtitles[String(c.id)] || '';

    /* The partner owns the skin: `.sfx` resolves the static tokens, but --o
       (anything clickable) and --m (header and banner) come from the saved Style
       config and have to be written onto the element.
       ⛔ No `font-family-inter` on this wrapper — `.sfx` declares the design
       system's typeface itself; see the note in storefront-tokens.css. */
    const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

    /* No PageLayout: it wraps children in `max-w-7xl mx-auto` and renders its
       own breadcrumb and heading, while `.br-wrap` is 1240px with a 40px gutter
       and owns the crumb and the h1. */
    return (
        <div ref={sfxRef} className="sfx">
            <ShareMeta
                title={storeDetails?.store_name ? `Collections | ${storeDetails.store_name}` : 'Collections'}
                image={collectionImageOf(ordered)}
            />
            <div className="br-wrap cl-wrap">
                <nav className="br-crumb" aria-label="Breadcrumb">
                    <a href="/">Home</a>
                    {' / '}
                    <span>Collections</span>
                </nav>

                <h1 className="br-h1">Collections</h1>
                <p className="br-intro">
                    Products grouped the way we actually recommend them — for a moment,
                    a stage, or a problem to solve.
                </p>

                {isLoading ? (
                    <CollectionRowSkeleton rows={2} />
                ) : ordered.length === 0 ? (
                    /* The day-one state for every new store. */
                    <div className="br-empty" style={{ marginTop: 20 }}>
                        <h2>No collections just yet</h2>
                        <p>
                            We&rsquo;re still putting these together. In the meantime,
                            everything we recommend is one click away.
                        </p>
                        <Button
                            variant="primary"
                            href="/all-products"
                            onClick={(e) => {
                                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                e.preventDefault();
                                navigate('/all-products');
                            }}
                        >
                            Shop all products
                        </Button>
                    </div>
                ) : (
                    ordered.map((collection) => (
                        <CollectionRow
                            key={collection.id}
                            name={collection.name}
                            description={describe(collection)}
                            /* The collection's own size, so five drawn out of
                               twelve still reads as twelve. */
                            count={typeof collection.product_count === 'number'
                                ? collection.product_count
                                : (collection.products || []).length}
                            products={collection.products}
                            href={`/collections/${collection.id}`}
                            onOpen={() => navigate(`/collections/${collection.id}`)}
                            renderProduct={(product) => {
                                const images = Array.isArray(product?.product_images)
                                    ? product.product_images
                                    : [];
                                const main = [...images]
                                    .sort((a, b) => Number(b.is_main) - Number(a.is_main))[0];

                                const isVendor = product?.is_multi_brand;
                                const brand = isVendor
                                    ? product?.vendor?.name
                                        || product?.shopify_vendor?.shopdot_vendor?.display_name
                                    : product?.brand?.name || product?.brand_name;
                                const brandId = product?.brand?.id;

                                return (
                                    <ProductCard
                                        name={product?.title}
                                        brand={brand}
                                        price={product?.price_msrp}
                                        image={main?.src}
                                        href={`/product/${product?.id}`}
                                        onClick={(e) => {
                                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                            e.preventDefault();
                                            navigate(`/product/${product?.id}`);
                                        }}
                                        brandHref={brandId
                                            ? `/brands/${brandId}${isVendor ? `/vendor/${product?.vendor?.id}/` : '/'}`
                                            : undefined}
                                        onBrandClick={brandId ? (e) => {
                                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                            e.preventDefault();
                                            navigate(`/brands/${brandId}${isVendor ? `/vendor/${product?.vendor?.id}/` : '/'}`);
                                        } : undefined}
                                        /* Same call as /all-products: this catalogue
                                           carries packshots, which `cover` crops. */
                                        objectFit="contain"
                                    />
                                );
                            }}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default Collections;
